import { useCallback, useEffect, useMemo, useState } from 'react';
import { GridActionsCellItem } from '@mui/x-data-grid';
import Permit from '../util/permit';
import { MainContainer, TopBarContainedBrandButton, TopBarContainer } from '../constants/component.constants';
import { ResponsiveRow } from '../constants/layout.constants';
import MuiDataGrid from '../components/modules/MuiDataGrid';
import MakeTransferModal from '../components/modals/accounting/bank accounts/makeTransferModal';
import { issueResponseMessage, issueWarnMessage } from '../actions/message';
import { formatDate, isNullUndefined, tryParseFloat } from '../util/util';
import { listCashToBankTransfers, reverseCashToBank, saveCashToBankTransfer } from '../services/bank.service';
import { History, Visibility } from '@mui/icons-material';
import ViewCashToBank from '../components/modals/accounting/bank accounts/viewCashToBank';
import ConfirmationModal from '../components/modals/confirmation';
import { useSelector } from 'react-redux';

export default function BankTransfers() {

  const [bankTransfers, setBankTransfers] = useState([]);
	const [makeTransfer, setMakeTransfer] = useState(false);
	const [selectedTransfer, setSelectedTransfer] = useState(null);
	const [viewTransfer, setViewTransfer] = useState(false);
	const [reverse, setReverse] = useState(false);
  const [loading, setLoading] = useState(false);
  const user = useSelector((state) => state.auth.user);

  const handleTransferClick = () => {
		setMakeTransfer(true);
  };

	const fetchCashToBankTransfers = useCallback(() => {
		setLoading(true);
		listCashToBankTransfers()
			.then((data) => {
				setBankTransfers(data);
			})
			.finally(() => {
				setLoading(false);
			})
	}, []);

	const saveTransfer = (transaction) => {
		if (isNullUndefined(transaction.amount)) {
			issueWarnMessage("Amount is required");
			return;
		}
		const amount = tryParseFloat(transaction.amount, 0);
		if (isNullUndefined(amount <= 0)) {
			issueWarnMessage("Amount is should be more than 0");
			return;
		}
		if (isNullUndefined(transaction.fromLedgerAccountId)) {
			issueWarnMessage("Source Ledger Account is required");
			return;
		}
		if (isNullUndefined(transaction.toBankAccountId)) {
			issueWarnMessage("Destination Bank Account is required");
			return;
		}
		if (isNullUndefined(transaction.toLedgerAccountId)) {
			issueWarnMessage("Destination Ledger Account is required");
			return;
		}
		if (isNullUndefined(transaction.description)) {
			issueWarnMessage("Memo is required");
			return;
		}
		// TODO: validate you cannot submit an amount greater than the balance on account

		saveCashToBankTransfer(transaction)
			.then((data) => {
				issueResponseMessage(data);
				// fetchCashToBankTransfers();
				setMakeTransfer(false);
			})
	}

	const cancelTransfer = () => {
		setMakeTransfer(false);
	}

	const handleViewClick = (transfer) => {
		setSelectedTransfer(transfer);
		setViewTransfer(true)
	}

	const handleReverseClick = (transfer) => {
		setSelectedTransfer(transfer);
		setReverse(true)
	}

	const handleReverse = () => {
		reverseCashToBank(selectedTransfer.id)
			.then((res) => {
				issueResponseMessage(res)
				setReverse(false)
				setSelectedTransfer(null)
			})
	}

	const getRowClassName = (params) => {
    const description = params.row?.description || "";
    if (description.includes("Reversed")) {
      return "voided";
    }
    return "";
  };


  const columns = useMemo(
    () => [
      {
        field: "entryDate",
        headerName: "DATE",
        width: 150,
        flex: 1,
        editable: false,
        valueFormatter: (row) => {
          return formatDate(row.value);
        },
      },
      {
        field: "description",
        headerName: "DESCRIPTION",
        width: 150,
        flex: 1,
        editable: false,
      },
      {
        field: "from",
        headerName: "FROM",
        width: 150,
        flex: 1,
        editable: false,
      },
      {
        field: "to",
        headerName: "TO",
        width: 150,
        flex: 1,
        editable: false,
      },
      {
        field: "amount",
        headerName: "AMOUNT",
        width: 150,
        flex: 1,
        editable: false,
      },
      {
        field: "actions",
        type: "actions",
        headerName: "ACTIONS",
        width: 100,
        getActions: (params) => {
          const row = params.row;
          const buttons = [
            <GridActionsCellItem
              icon={<Visibility />}
              label="View"
              onClick={() => handleViewClick(params.row)}
              showInMenu
            />,
          ];

          if (row?.description.includes("Reversed")) {
            return buttons;
          } else {
            buttons.push(
              <GridActionsCellItem
                icon={<History />}
                label="Reverse Transfer"
                onClick={() => handleReverseClick(params.row)}
                showInMenu
              />
            );
            return buttons;
          }
        },
      },
    ],
    []
  );

	useEffect(() => {
		if(!makeTransfer && !reverse) {
			fetchCashToBankTransfers();
		}
	}, [fetchCashToBankTransfers, makeTransfer, reverse, user.proxyFacility])

	return (
    <Permit
      roles="BLDIR,ACC,BLDMG,CS"
      noPermittedServiceMessage={{
        title: "Access Denied",
        body: (
          <>
            Your current role is not permitted to view this page. <br /> Please
            contact your system admin if you feel this is an error.
          </>
        ),
      }}
    >
      <MainContainer>
        <TopBarContainer
          container
          sx={{
            justifyContent: { xs: "center", lg: "space-between" },
            width: { xs: "100%", md: "80%" },
            padding: "0 10px",
            boxShadow: "0 4px 5px 0 rgba(0,0,0,0.5)",
          }}
        >
          <ResponsiveRow
            sx={{
              justifyContent: { md: "flex-end", xs: "center" },
              width: "100%",
              height: "auto",
            }}
          >
            <TopBarContainedBrandButton
              variant="contained"
              sx={{
                width: { xs: "100%", lg: "250px" },
                height: "100%",
              }}
              // type="submit"
              onClick={handleTransferClick}
            >
              MAKE TRANSFER
            </TopBarContainedBrandButton>
          </ResponsiveRow>
        </TopBarContainer>

        <br />

        <MuiDataGrid
          dataGridColumns={columns}
          dataGridRows={bankTransfers}
          serverPagination={true}
          // handlePageLoad={setPaginationModel}
          // handleSelectedRows={setSelectionModel}
          // currentSelectionModel={selectionModel}
          noSelection={true}
          loading={loading}
          height="60vh"
					getRowClassName={getRowClassName}
        />

        <MakeTransferModal
          makeTransfer={makeTransfer}
          setMakeTransfer={setMakeTransfer}
          handleSaveClicked={saveTransfer}
          handleCloseClicked={cancelTransfer}
        />
        <ViewCashToBank
          open={viewTransfer}
          close={() => setViewTransfer(false)}
          transfer={selectedTransfer}
        />
        {reverse && selectedTransfer && (
          <ConfirmationModal
            showConfirmationModal={reverse}
            negativeAction={() => setReverse(false)}
            severity="error"
            body={
              <div>
                Are you sure you would like to reverse the cash to bank
                transaction?
                <br /> {selectedTransfer?.from} {"->"} {selectedTransfer?.to}
              </div>
            }
            positiveText="Reverse"
            title="Reverse Cash To Bank"
            positiveAction={handleReverse}
          />
        )}
      </MainContainer>
    </Permit>
  );
}
